import React, {useEffect, useRef, useState} from 'react';
import {FiAlertCircle, FiCheckCircle, FiChevronDown, FiChevronUp, FiLoader, FiMessageSquare, FiSend} from 'react-icons/fi';
import {examFeedbackApi, getErrorMessage} from '../services/api';
import MathText from '../components/MathText';
import {
    appealReasonLabels,
    eligibilityReasonLabels,
    feedbackCategoryLabels,
    formatFeedbackDate,
    getConflictCode,
    makeIdempotencyKey,
} from './examFeedbackUtils';

const initialFeedback = {scope: 'result', category: 'accuracy', rating: 3, comment: '', consent_for_improvement: false};
const initialAppeal = {reason: 'grading_error', description: ''};

export default function StudentFeedbackActions({attempt, answer, onChanged}) {
    const [expanded, setExpanded] = useState(false);
    const [mode, setMode] = useState('feedback');
    const [eligibility, setEligibility] = useState(null);
    const [loadingEligibility, setLoadingEligibility] = useState(false);
    const [feedback, setFeedback] = useState(initialFeedback);
    const [appeal, setAppeal] = useState(initialAppeal);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const idempotencyKey = useRef({feedback: '', appeal: ''});
    const triggerRef = useRef(null);
    const attemptId = attempt?.id;
    const answerId = answer?.id;
    const ratingLabelId = `ef-rating-label-${attemptId}-${answerId ?? 'overall'}`;

    useEffect(() => {
        if (!expanded) return undefined;
        const onKeyDown = (event) => {
            if (event.key !== 'Escape' || submitting) return;
            setExpanded(false);
            window.setTimeout(() => triggerRef.current?.focus(), 0);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [expanded, submitting]);

    useEffect(() => {
        if (!expanded || mode !== 'appeal' || !attemptId || eligibility) return undefined;
        let active = true;
        setLoadingEligibility(true);
        setMessage(null);
        examFeedbackApi.student.eligibility({attemptId, answerId})
            .then((data) => active && setEligibility(data))
            .catch((error) => active && setMessage({type: 'error', text: getErrorMessage(error)}))
            .finally(() => active && setLoadingEligibility(false));
        return () => { active = false; };
    }, [expanded, mode, attemptId, answerId, eligibility]);

    const toggle = () => {
        const nextExpanded = !expanded;
        setExpanded(nextExpanded);
        if (!nextExpanded) setEligibility(null);
        setMessage(null);
    };
    const selectMode = (nextMode) => {
        setMode(nextMode);
        setMessage(null);
    };
    const close = () => {
        if (submitting) return;
        setExpanded(false);
        setEligibility(null);
        setMessage(null);
        window.setTimeout(() => triggerRef.current?.focus(), 0);
    };
    const notifyChanged = () => {
        window.dispatchEvent(new CustomEvent('exam-feedback-changed'));
        onChanged?.();
    };
    const submitFeedback = async (event) => {
        event.preventDefault();
        if (feedback.comment.trim().length < 3) {
            setMessage({type: 'error', text: 'توضیح بازخورد باید حداقل ۳ کاراکتر باشد.'});
            return;
        }
        if (!idempotencyKey.current.feedback) idempotencyKey.current.feedback = makeIdempotencyKey('feedback', attemptId, answerId);
        setSubmitting(true);
        setMessage(null);
        try {
            await examFeedbackApi.student.createFeedback({
                attempt_id: attemptId,
                ...(answerId == null ? {} : {answer_id: answerId}),
                scope: answerId == null ? feedback.scope : 'question',
                category: feedback.category,
                rating: Number(feedback.rating),
                comment: feedback.comment.trim(),
                consent_for_improvement: feedback.consent_for_improvement,
            }, idempotencyKey.current.feedback);
            idempotencyKey.current.feedback = '';
            setFeedback(initialFeedback);
            notifyChanged();
            setMessage({
                type: 'success',
                text: `بازخورد ثبت شد · ${formatFeedbackDate(new Date().toISOString())} · از منوی «بازخورد و اعتراض‌ها» قابل پیگیری است.`,
            });
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally { setSubmitting(false); }
    };
    const submitAppeal = async (event) => {
        event.preventDefault();
        if (appeal.description.trim().length < 10) {
            setMessage({type: 'error', text: 'شرح اعتراض باید حداقل ۱۰ کاراکتر باشد.'});
            return;
        }
        if (!eligibility?.eligible) return;
        if (!idempotencyKey.current.appeal) idempotencyKey.current.appeal = makeIdempotencyKey('appeal', attemptId, answerId);
        setSubmitting(true);
        setMessage(null);
        try {
            await examFeedbackApi.student.createAppeal({
                attempt_id: attemptId,
                ...(answerId == null ? {} : {answer_id: answerId}),
                scope: answerId == null ? 'overall_result' : 'answer',
                reason: appeal.reason,
                description: appeal.description.trim(),
            }, idempotencyKey.current.appeal);
            idempotencyKey.current.appeal = '';
            setAppeal(initialAppeal);
            notifyChanged();
            setEligibility((current) => ({...current, eligible: false, reason: 'OPEN_APPEAL_ALREADY_EXISTS'}));
            setMessage({
                type: 'success',
                text: `اعتراض ثبت و برای استاد ارسال شد · ${formatFeedbackDate(new Date().toISOString())} · نتیجه رسیدگی در منوی «بازخورد و اعتراض‌ها» نمایش داده می‌شود.`,
            });
        } catch (error) {
            const conflict = getConflictCode(error);
            if (conflict === 'OPEN_APPEAL_ALREADY_EXISTS') {
                notifyChanged();
                setEligibility((current) => ({...current, eligible: false, reason: conflict}));
                setMessage({type: 'error', text: eligibilityReasonLabels[conflict]});
            } else setMessage({type: 'error', text: getErrorMessage(error)});
        } finally { setSubmitting(false); }
    };

    return <div className={`ef-student-actions ${answer ? 'compact' : 'overall'}`}>
        <button ref={triggerRef} type="button" className="exam-btn ghost ef-combined-action" aria-expanded={expanded} aria-controls={`ef-action-panel-${attemptId}-${answerId ?? 'overall'}`} onClick={toggle}>
            <FiMessageSquare/> بازخورد و اعتراض {expanded ? <FiChevronUp/> : <FiChevronDown/>}
        </button>
        {expanded && <section id={`ef-action-panel-${attemptId}-${answerId ?? 'overall'}`} className="ef-action-panel" aria-label={answer ? 'اقدامات پاسخ سؤال' : 'اقدامات نتیجه آزمون'}>
            <header><div><strong>{answer ? 'درباره این پاسخ' : 'درباره نتیجه آزمون'}</strong><small>نوع درخواست را انتخاب کنید.</small></div><button type="button" onClick={close}>بستن</button></header>
            <div className="ef-action-tabs" role="tablist"><button type="button" role="tab" aria-selected={mode === 'feedback'} className={mode === 'feedback' ? 'active' : ''} onClick={() => selectMode('feedback')}><FiMessageSquare/> بازخورد</button><button type="button" role="tab" aria-selected={mode === 'appeal'} className={mode === 'appeal' ? 'active' : ''} onClick={() => selectMode('appeal')}><FiAlertCircle/> اعتراض</button></div>
            <p className={`ef-mode-help ${mode}`}><span>{mode === 'feedback' ? <FiMessageSquare/> : <FiAlertCircle/>}</span><span><strong>{mode === 'feedback' ? 'برای پیشنهاد و بیان تجربه شما' : 'برای درخواست بازبینی نمره'}</strong><small>{mode === 'feedback' ? 'بازخورد مستقیماً باعث تغییر نمره نمی‌شود.' : 'اعتراض توسط استاد بررسی می‌شود و ممکن است نمره تغییر کند.'}</small></span></p>
            {message && <div className={`ef-message ${message.type}`} role="status" aria-live="polite">{message.type === 'success' && <FiCheckCircle/>}{message.text}</div>}
            {mode === 'feedback' ? <form onSubmit={submitFeedback}>
                {!answerId && <label>حوزه بازخورد<select value={feedback.scope} onChange={(event) => setFeedback({...feedback, scope: event.target.value})}><option value="result">نتیجه آزمون</option><option value="grading">فرایند تصحیح</option><option value="process">فرایند برگزاری آزمون</option></select></label>}
                <div className="ef-form-row"><label>موضوع<select value={feedback.category} onChange={(event) => setFeedback({...feedback, category: event.target.value})}>{Object.entries(feedbackCategoryLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label><div className="ef-rating" role="group" aria-labelledby={ratingLabelId}><span id={ratingLabelId} className="ef-rating-label">امتیاز</span><div>{[1, 2, 3, 4, 5].map((rating) => <button key={rating} type="button" className={Number(feedback.rating) === rating ? 'active' : ''} aria-pressed={Number(feedback.rating) === rating} aria-label={`امتیاز ${rating.toLocaleString('fa-IR')} از ۵`} onClick={() => setFeedback({...feedback, rating})}>{rating.toLocaleString('fa-IR')}</button>)}</div><small>{Number(feedback.rating).toLocaleString('fa-IR')} از ۵</small></div></div>
                <label>توضیحات<textarea rows="4" minLength="3" maxLength="4000" required value={feedback.comment} onChange={(event) => setFeedback({...feedback, comment: event.target.value})} placeholder="چه چیزی می‌تواند بهتر شود؟"/></label>
                <label className="ef-check"><input type="checkbox" checked={feedback.consent_for_improvement} onChange={(event) => setFeedback({...feedback, consent_for_improvement: event.target.checked})}/><span><strong>اجازه استفاده برای بهبود سامانه</strong><small>فقط محتوای همین بازخورد برای تحلیل کیفیت استفاده می‌شود.</small></span></label>
                <footer><button type="button" onClick={close}>انصراف</button><button className="primary" disabled={submitting}>{submitting ? <FiLoader className="spin"/> : <FiSend/>} ثبت بازخورد</button></footer>
            </form> : <form onSubmit={submitAppeal}>
                {loadingEligibility ? <div className="ef-loading"><FiLoader className="spin"/> در حال بررسی امکان اعتراض…</div> : <>
                    <div className="ef-appeal-context"><div><small>آزمون</small><strong>{attempt?.exam_title || 'نتیجه آزمون'}</strong></div><div><small>نمره فعلی</small><strong>{attempt?.score ?? '—'} از {attempt?.max_score ?? '—'}</strong></div>{answer && <><div className="wide"><small>سؤال</small><strong><MathText text={answer.prompt || '—'}/></strong></div><div className="wide"><small>پاسخ شما</small><p><MathText text={answer.answer_text || 'بدون پاسخ'}/></p></div><div><small>نمره پاسخ</small><strong>{answer.awarded_points ?? '—'} از {answer.points ?? '—'}</strong></div></>}</div>
                    {eligibility?.deadline && <div className="ef-deadline"><span>مهلت ثبت اعتراض</span><strong>{formatFeedbackDate(eligibility.deadline)}</strong></div>}
                    {eligibility && !eligibility.eligible && <div className="ef-message error">{eligibilityReasonLabels[eligibility.reason] || eligibility.reason || 'امکان ثبت اعتراض وجود ندارد.'}</div>}
                    <label>دلیل اعتراض<select disabled={!eligibility?.eligible} value={appeal.reason} onChange={(event) => setAppeal({...appeal, reason: event.target.value})}>{Object.entries(appealReasonLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
                    <label>شرح اعتراض<textarea disabled={!eligibility?.eligible} rows="4" minLength="10" maxLength="5000" required value={appeal.description} onChange={(event) => setAppeal({...appeal, description: event.target.value})} placeholder="دقیقاً توضیح دهید چه چیزی باید بازبینی شود…"/></label>
                    <p className="ef-hint">ثبت اعتراض به معنی پذیرش یا تغییر خودکار نمره نیست.</p>
                    <footer><button type="button" onClick={close}>انصراف</button><button className="primary" disabled={submitting || !eligibility?.eligible}>{submitting ? <FiLoader className="spin"/> : <FiSend/>} ثبت اعتراض</button></footer>
                </>}
            </form>}
        </section>}
    </div>;
}

